import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Storefront } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { KIOSK_PILOT_EMAIL } from '../constants/kiosk';
import { APP_DISPLAY_NAME } from '../constants/branding';

const KIOSK_PILOT_PASSWORD = 'Metals';

const Login = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect') || '/';
  const sessionExpired = searchParams.get('session') === 'expired';
  const inactiveAccount = searchParams.get('reason') === 'inactive';
  const { login, resendVerification, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showResendInvite, setShowResendInvite] = useState(false);
  const [resendInviteMessage, setResendInviteMessage] = useState('');
  const [resendDevLink, setResendDevLink] = useState('');
  const [resendInviteBusy, setResendInviteBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kioskLoading, setKioskLoading] = useState(false);
  const [kioskPublicEnabled, setKioskPublicEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/public/kiosk-enabled')
      .then((res) => {
        if (!cancelled) setKioskPublicEnabled(res.data?.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setKioskPublicEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Redirect if already authenticated (kiosk pilot goes to kiosk via App.js root route)
  if (isAuthenticated) {
    return <Navigate to={redirectTo.startsWith('/') ? redirectTo : '/'} replace />;
  }

  const handleKioskLogin = async () => {
    setKioskLoading(true);
    setError('');
    try {
      const result = await login(KIOSK_PILOT_EMAIL, KIOSK_PILOT_PASSWORD);
      if (result.success) {
        navigate('/time-tracking/kiosk', { replace: true });
      } else {
        setError(result.message || 'Kiosk login failed');
      }
    } catch (err) {
      setError('Could not sign in. Check that the kiosk user exists.');
    } finally {
      setKioskLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (e.target.name === 'email') {
      setError('');
      setShowResendInvite(false);
      setResendInviteMessage('');
      setResendDevLink('');
    } else if (e.target.name === 'password' && error && !showResendInvite) {
      setError('');
    }
  };

  const handleResendInvite = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) {
      setResendInviteMessage('Enter your email above, then try again.');
      return;
    }
    setResendInviteBusy(true);
    setResendInviteMessage('');
    setResendDevLink('');
    const result = await resendVerification(email);
    setResendInviteBusy(false);
    if (result.success) {
      if (result.verifyUrl) {
        setResendInviteMessage(
          'This server did not send an email. If an unverified account exists for that address, use the link below to verify.'
        );
        setResendDevLink(result.verifyUrl);
      } else {
        setResendInviteMessage(
          'If this email has an unverified account, we sent a new verification link. If you do not see it within a few minutes, check your spam folder.'
        );
      }
    } else {
      setResendInviteMessage(result.message || 'Could not send the email. Try again later.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');
    setShowResendInvite(false);
    setResendInviteMessage('');
    setResendDevLink('');

    try {
      const result = await login(formData.email, formData.password);

      if (!result.success) {
        setError(result.message);
        setShowResendInvite(result.code === 'EMAIL_NOT_VERIFIED');
      }
    } catch (err) {
      setError('Unable to sign in. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Card
          elevation={3}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              py: 4,
              px: 3,
            }}
          >
          <Typography
            component="h1"
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              letterSpacing: '0.28em',
              color: 'primary.dark',
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
            }}
          >
            {APP_DISPLAY_NAME}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to your account
          </Typography>

          {inactiveAccount && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Your account is inactive. Contact an administrator if you need access.
            </Alert>
          )}
          {sessionExpired && !inactiveAccount && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              Your session has expired. Sign in again to continue. Unsaved changes on this page were not saved.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              <Typography variant="body2" component="div" sx={{ mb: showResendInvite ? 1 : 0 }}>
                {error}
              </Typography>
              {showResendInvite && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResendInvite}
                    disabled={resendInviteBusy}
                    sx={{ cursor: resendInviteBusy ? 'default' : 'pointer' }}
                  >
                    Re-send invite
                  </Link>
                  {resendInviteBusy ? <CircularProgress size={16} /> : null}
                </Box>
              )}
              {resendInviteMessage ? (
                <Box sx={{ mt: showResendInvite ? 1 : 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {resendInviteMessage}
                  </Typography>
                  {resendDevLink ? (
                    <Link
                      href={resendDevLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ wordBreak: 'break-all', display: 'block', mt: 0.5 }}
                    >
                      {resendDevLink}
                    </Link>
                  ) : null}
                </Box>
              ) : null}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>

            <Typography variant="body2" align="center" sx={{ mb: 1 }}>
              <Link component={RouterLink} to="/register" underline="hover">
                Create an account
              </Link>
            </Typography>

            {kioskPublicEnabled && (
              <>
                <Divider sx={{ my: 2 }}>or</Divider>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Shop computer — clock in/out only
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Storefront />}
                  onClick={handleKioskLogin}
                  disabled={kioskLoading || loading}
                  sx={{ mb: 2 }}
                >
                  {kioskLoading ? <CircularProgress size={20} /> : 'Enter Kiosk Mode'}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  Uses saved login; opens time clock only.
                </Typography>
              </>
            )}
          </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
